/* eslint-disable no-magic-numbers -- surface tokens are named by number */

import styled from 'styled-components';
import { transition } from '../themes/mainTheme';

// Tallest the calendar gets: a single month with the time panel is ~335px, a
// range month and the year grid are shorter. The reveal is a max-height rather
// than a `0fr`/`1fr` grid row because clipping the row — on the track's item or
// on the container — collapses this calendar's track to zero, so the row never
// opens.
// ponytail: a bound, not a measurement. A taller calendar (numberOfMonths
// stacked vertically, say) would be clipped; measure the content with a
// ResizeObserver if that ever ships.
const CALENDAR_MAX_HEIGHT = 400;

export const Root = styled.div<{ $open?: boolean }>`
  position: relative;
  z-index: 10;
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? `${CALENDAR_MAX_HEIGHT}px` : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  /* A clipped calendar still holds focusable days, so visibility is what takes
     it out of the tab order. It flips at the far end of the close and the near
     end of the open, so it never hides a frame that is still moving. */
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  /* Rises out of the trigger it belongs to — the same 4px the tooltip uses. */
  translate: 0 ${({ $open }) => ($open ? '0' : '-4px')};
  transition:
    ${transition('max-height', 'opacity', 'translate')},
    visibility 0s ${({ $open, theme }) => ($open ? '0s' : theme.motion.duration.default)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Shell = styled.div`
  font-family: ${props => props.theme.font};
  position: relative;

  .rmdp-wrapper,
  .rmdp-shadow,
  .rmdp-calendar {
    position: static !important;
    transform: none !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .rmdp-shadow {
    background: ${props => props.theme.surface[2]};
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin: 0 auto;
  }

  .rmdp-top-class {
    padding: 15px;
  }

  .rmdp-calendar {
    padding: 0;
  }

  .rmdp-day-picker {
    padding: 0;

    & > div {
      padding: 5px 20px 0;
    }
  }

  .rmdp-border-left {
    border: none;
  }

  .rmdp-header {
    z-index: 2;
  }

  .rmdp-header-values,
  .rmdp-day {
    color: ${props => props.theme.text.primary};
  }

  .rmdp-header-values > span {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .rmdp-header-values > span::after {
    content: "▾";
    font-size: 12px;
    line-height: 1;
    color: ${props => props.theme.text.tertiary};
    transform: translateY(1px);
  }

  .rmdp-arrow-container {
    position: absolute;
  }

  .rmdp-arrow {
    border-color: ${props => props.theme.text.primary};
    height: 10px;
    width: 10px;
  }

  .rmdp-left {
    left: 10px;
  }

  .rmdp-right {
    right: 10px;
  }

  .rmdp-arrow-container:hover {
    box-shadow: none;
    background: none;
  }

  .rmdp-arrow-container:hover .rmdp-arrow {
    border-color: ${props => props.theme.text.brand};
  }

  /* Month and year pickers overlay the day grid, so they need an opaque surface. */
  .rmdp-month-picker,
  .rmdp-year-picker {
    z-index: 5;
    background: ${props => props.theme.surface[2]};
    border: none;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
  }

  .rmdp-week-day {
    color: ${props => props.theme.text.tertiary};
    font-weight: 400;
  }

  .rmdp-day.rmdp-deactive,
  .rmdp-day.rmdp-disabled {
    color: ${props => props.theme.text.disabled};
  }

  /* Today is marked with brand text, not a fill — the fill means "selected". */
  .rmdp-today span:not(.highlight) {
    background-color: transparent;
    color: ${props => props.theme.text.brand};
  }

  .rmdp-ep-arrow {
    display: none;
  }

  .rmdp-day.rmdp-selected span:not(.highlight) {
    box-shadow: none;
    background-color: ${props => props.theme.fill.brand};
    color: ${props => props.theme.text.inverted};
  }

  /* ponytail: secondary green has no fill token yet — text.success is the same
     primitive (green 200), already used as a background in aiChat.pill. */
  .rmdp-range,
  .rmdp-range-hover {
    box-shadow: none;
    color: ${props => props.theme.text.inverted};
    background-color: ${props => props.theme.fill.success};
    border-radius: 0 !important;
  }

  .rmdp-day span {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 4px;
  }

  .rmdp-range span,
  .rmdp-range-hover span {
    border-radius: 0 !important;
  }

  .rmdp-range.start span,
  .rmdp-range-hover.start span,
  .rmdp-range.end span,
  .rmdp-range-hover.end span {
    border-radius: 4px !important;
    background-color: ${props => props.theme.fill.brand} !important;
    color: ${props => props.theme.text.inverted} !important;
  }

  .rmdp-range.start.end span,
  .rmdp-range-hover.start.end span {
    border-radius: 4px !important;
  }

  .rmdp-day:not(.rmdp-disabled, .rmdp-day-hidden) span:hover {
    background-color: ${props => props.theme.text.success};
    color: ${props => props.theme.text.inverted};
  }

  .rmdp-range.start:hover span,
  .rmdp-range.end:hover span,
  .rmdp-range-hover.start:hover span,
  .rmdp-range-hover.end:hover span {
    background-color: ${props => props.theme.fill.brand} !important;
    color: ${props => props.theme.text.inverted} !important;
  }
  /* The react-multi-date-picker time plugin ships light chrome and stacks its
     up/down arrows with absolute offsets that collapse inside Shell, so the
     unit is laid out directly here. */
  /* The library fences a plugin panel off from the grid with its own hairline,
     in a light-theme blue-grey. The panel already reads as its own block. */
  .rmdp-border-top {
    border-top: none;
  }

  .rmdp-border-bottom {
    border-bottom: none;
  }

  .rmdp-time-picker {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px 8px;
  }

  .rmdp-time-picker > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .rmdp-time-picker .rmdp-arrow-container {
    position: static;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: transparent;
    box-shadow: none;
  }

  .rmdp-time-picker .rmdp-arrow {
    border: solid ${props => props.theme.text.tertiary};
    border-width: 0 2px 2px 0;
    height: 7px;
    width: 7px;
    margin: 0;
  }

  .rmdp-time-picker .rmdp-arrow-container:hover {
    background: transparent;
    box-shadow: none;
  }

  .rmdp-time-picker .rmdp-arrow-container:hover .rmdp-arrow {
    border-color: ${props => props.theme.text.primary};
  }

  .rmdp-time-picker input {
    border: 1px solid ${props => props.theme.border.decorative};
    border-radius: ${props => props.theme.form.border.radius}px;
    padding: 4px;
    width: 44px;
    text-align: center;
    color: ${props => props.theme.text.primary};
    background: ${props => props.theme.form.dropdown.menu.default};
    font-family: ${props => props.theme.font};
    font-size: 14px;
  }

  .rmdp-time-picker input:focus {
    border-color: ${props => props.theme.form.input.focus.border};
    outline: none;
  }

  .rmdp-time-picker .dvdr {
    color: ${props => props.theme.text.tertiary};
  }

`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  /* Scrim over the calendar, not a palette colour. */
  background: ${props => props.theme.scrim};
  backdrop-filter: blur(2px);
  display: grid;
  place-items: center;
  z-index: 10;
  color: ${props => props.theme.text.brand};
`;
